import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { ChevronDown, Info, Plus } from "lucide-react";
import Slider from "react-slick";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import ActionPlanProgress from "../../../../components/ActionPlanProgress";
import ArrowIco from "../../../../assets/careerBuddy/arrow_ic.svg";
import ArrowPlayIco from "../../../../assets/actionPlan/play_arrow_filled.svg";
import ActionStepIco from "../../../../assets/actionPlan/ic_action_step.png";
import ShineIco from "../../../../assets/strength/ic_shine.svg";
import ActivityIco from "../../../../assets/strength/ic_activity.svg";
import DoWellIco from "../../../../assets/strength/ic_do_well.svg";
import LightBulbIco from "../../../../assets/strength/ic_light_bulb.png";
import { isAccessFeature } from "../../../../common/utils";
import useAffiliateCodeModal from "../../../../hooks/useAffiliateCodeModal";
import useRecommendations from "../../../../hooks/useRecommendations";
import { AffiliateCodeModal, ChoosePlanFirstModal } from "../../../../modals";
import BaseModal from "../../../../modals/BaseModal";
import FindCareer from "../../../../modals/FindCareer";
import { authUser } from "../../../../redux/selectors/authSelectors";
import Card from "../Recommendations/components/Card/Card";
import "./style.scss";
import {
  deleteTerms,
  downloadAction,
  generateIdeas,
  initCompanions,
  initGetTerms,
  initSetProfile,
  initStrengths,
  reorderActionTerm,
  setTerms,
} from "../../../../redux/actions";
import ActionIdeas from "./ActionIdeas";
import ActionPlanTerm from "./ActionPlanTerm";
import NextStepsSection from "./NextStepsSection";
import PlanGenerationModal from "./PlanGenerationModal";
import PlanEmptyState from "./PlanEmptyState";
import { buildGenerateIdeasPrompt } from "./buildGenerateIdeasPrompt";
import { LocalStorage } from "../../../../common/localStorage";
import Loader from "../../../../components/Loader";

const DEFAULT_TASK_STATUS = "not_started";
const TASK_STATUS_OPTIONS = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];
const DIRECTION_RATING_OPTIONS = Array.from(
  { length: 10 },
  (_, index) => index + 1,
);
const ACTION_STEP_INITIAL_FORM = {
  idea: "",
  linked_career: "",
  suggested_bucket: "",
  status: DEFAULT_TASK_STATUS,
};

const TERMS_DRAG_DROP = [
        {
          title: "Research one real person in this field and map their path",
          career: "Any Career",
          isStatic:true,
          id: 1,
        },
        {
          title: "Try a small hands-on activity related to this career",
          career: "Any Career",
          id: 2,
        },
        {
          title: "Talk to someone about this career",
          career: "Any Career",
          id: 3,
        },
        {
          title: "Explore schools, programs, or certifications for this path",
          career: "Any Career",
          id: 4,
        },
];

const ACTION_PLAN_IDEAS_STORAGE_KEY = "action_plan_generated_ideas";
const ACTION_PLAN_UI_STARTED_KEY = "action_plan_ui_started";

const PLAN_GENERATION_STEPS = [
  {
    progress: 10,
    description: "Using your career interests...",
  },
  {
    progress: 25,
    description: "Adding your strengths...",
  },
  {
    progress: 50,
    description: "Your ideas are ready. Drag them into a time bucket when you continue.",
  },
];

const STRENGTH_CARDS = [
  { icon: ShineIco, alt: "Shine" },
  { icon: DoWellIco, alt: "Do well" },
  { icon: ActivityIco, alt: "Activity" },
];

const getStatusClassName = (status) =>
  String(status).toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");

const createTask = ({ id, title, career, status = DEFAULT_TASK_STATUS }) => ({
  id,
  title,
  career,
  status,
});

const toNumericRanking = (ranking, fallback) => {
  const parsedRanking = Number(ranking);

  return Number.isFinite(parsedRanking) ? parsedRanking : fallback;
};

const normalizeTaskOrder = (tasks = []) =>
  tasks.map((task, index) => ({
    ...task,
    ranking: index + 1,
  }));

const sortTasksByRanking = (tasks = []) =>
  [...tasks]
    .map((task, index) => ({
      task,
      index,
      ranking: toNumericRanking(task.ranking, Number.MAX_SAFE_INTEGER),
    }))
    .sort((left, right) => left.ranking - right.ranking || left.index - right.index)
    .map(({ task }) => task);

const moveTaskInList = (tasks = [], draggedTaskId, targetTaskId = null) => {
  const currentTasks = [...tasks];
  const draggedIndex = currentTasks.findIndex((task) => task.id === draggedTaskId);

  if (draggedIndex === -1) {
    return currentTasks;
  }

  const [draggedTask] = currentTasks.splice(draggedIndex, 1);

  if (!targetTaskId) {
    currentTasks.push(draggedTask);
    return normalizeTaskOrder(currentTasks);
  }

  const targetIndex = currentTasks.findIndex((task) => task.id === targetTaskId);

  if (targetIndex === -1) {
    currentTasks.push(draggedTask);
    return normalizeTaskOrder(currentTasks);
  }

  currentTasks.splice(targetIndex, 0, draggedTask);
  return normalizeTaskOrder(currentTasks);
};

const getSavedTaskId = (setTermsData) => {
  const data = setTermsData?.data || setTermsData;
  const actionPlans = Array.isArray(data?.action_plans)
    ? data.action_plans
    : [];
  const candidates = [
    data?.id,
    data?.action_plan?.id,
    data?.actionPlan?.id,
    actionPlans[0]?.id,
    data?.result?.id,
    data?.results?.id,
  ];

  const savedId = candidates.find((id) => id !== undefined && id !== null);
  return savedId !== undefined && savedId !== null ? String(savedId) : null;
};

const getCareerOptionsFromCareer = (career) => {
  if (!career) {
    return [];
  }

  const maskedTitles =
    career?.masked_titles || career?.masked_title || career?.maskedTitle;

  if (Array.isArray(maskedTitles) && maskedTitles.length) {
    return maskedTitles.filter(Boolean);
  }

  if (typeof maskedTitles === "string" && maskedTitles.trim()) {
    return [maskedTitles.trim()];
  }

  const fallback =
    career?.unique_title || career?.name || career?.title || career?.career;
  return fallback ? [fallback] : [];
};

const mapGeneratedIdeaList = (ideas) =>
  (Array.isArray(ideas) ? ideas : []).map((idea, index) => ({
    id:
      idea.id ||
      `${String(idea.linked_career || idea.career || "idea")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}-${index}`,
    title: idea.plan || idea.title || idea.step || idea.response || "",
    career: idea.linked_career || idea.career || "Any Career",
  }));

const createEmptyActionPlanTerms = () => [
  {
    id: "short_term",
    title: "Short-Term",
    duration: "0 - 6 months",
    tasks: [],
  },
  {
    id: "medium_term",
    title: "Medium-Term",
    duration: "6 - 12 months",
    tasks: [],
  },
  {
    id: "long_term",
    title: "Long-Term",
    duration: "12+ months",
    tasks: [],
  },
];

export default function ActionPlan() {
  const router = useNavigate();
  const dispatch = useDispatch();
  const { user } = useOutletContext();
  const { role } = useSelector(authUser);
  const actionPlanState = useSelector((state) => state.actionPlanReducer);
  const strengthsState = useSelector((state) => state.strengthsReducer);
  const isErrorPricing = isAccessFeature({ ...user, role });
  const { results } = useRecommendations();

  const {
    affiliateCodeModal,
    choosePlanFirstOpen,
    closeChoosePlanFirstModal,
    confirmChoosePlanFirstModal,
    closeAffiliateCodeModal,
    openAffiliateCodeModal,
    setAffiliateCode,
    submitAffiliateCode,
  } = useAffiliateCodeModal(user);

  const [isOpenAddSearchCareer, setIsOpenAddSearchCareer] = useState(false);
  const [isPlanGenerationOpen, setIsPlanGenerationOpen] = useState(false);
  const [showProgressInfo, setShowProgressInfo] = useState(false);
  const [planGenerationStep, setPlanGenerationStep] = useState(0);
  const [openAccordionId, setOpenAccordionId] = useState("short_term");
  const [actionPlanTerms, setActionPlanTerms] = useState([]);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [directionRating, setDirectionRating] = useState(
    user?.certainty ? Number(user.certainty) : null,
  );
  const [actionPlanIdeas, setActionPlanIdeas] = useState([]);
  const [draggedIdeaId, setDraggedIdeaId] = useState(null);
  const [dragOverTermId, setDragOverTermId] = useState("");
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [draggedTaskTermId, setDraggedTaskTermId] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [touchDraggedTaskId, setTouchDraggedTaskId] = useState(null);
  const [touchDropTargetTaskId, setTouchDropTargetTaskId] = useState(null);
  const [touchDraggedIdeaId, setTouchDraggedIdeaId] = useState(null);
  const [actionStepModalTermId, setActionStepModalTermId] = useState("");
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const actionPlanTermsRef = useRef(actionPlanTerms);
  const touchTaskDragRef = useRef(null);
  const touchIdeaDragRef = useRef(null);

  const mapApiTerms = (data) => {
    const plans = Array.isArray(data?.action_plans) ? data.action_plans : [];

    const bucketTasks = plans.reduce(
      (acc, item) => {
        const bucket = item.suggested_bucket || "short_term";
        const task = {
          id: item.id != null ? String(item.id) : `${bucket}-${Date.now()}`,
          title: item.idea || "",
          career: item.linked_career || "Any Career",
          status: item.status || DEFAULT_TASK_STATUS,
          ranking: item.ranking,
        };

        return {
          ...acc,
          [bucket]: [...(acc[bucket] || []), task],
        };
      },
      {
        short_term: [],
        medium_term: [],
        long_term: [],
      },
    );

    return [
      {
        id: "short_term",
        title: "Short-Term",
        duration: "0 - 6 months",
        tasks: normalizeTaskOrder(sortTasksByRanking(bucketTasks.short_term)),
      },
      {
        id: "medium_term",
        title: "Medium-Term",
        duration: "6 - 12 months",
        tasks: normalizeTaskOrder(sortTasksByRanking(bucketTasks.medium_term)),
      },
      {
        id: "long_term",
        title: "Long-Term",
        duration: "12+ months",
        tasks: normalizeTaskOrder(sortTasksByRanking(bucketTasks.long_term)),
      },
    ];
  };

  const [actionStepForm, setActionStepForm] = useState(
    ACTION_STEP_INITIAL_FORM,
  );
  const [actionStepErrors, setActionStepErrors] = useState({});
  const [actionStepEditingTask, setActionStepEditingTask] = useState(null);
  const [actionStepModalState, setActionStepModalState] = useState({
    isLoading: false,
    error: null,
    isSuccess: false,
  });
  const [openStrengthCard, setOpenStrengthCard] = useState(null);
  const [hasRequestedTerms, setHasRequestedTerms] = useState(false);
  const [planUiStarted, setPlanUiStarted] = useState(false);
  const strengthSlidesRef = useRef([]);
  const strengths = strengthsState?.strengths;
  const safeStrengths = strengths || {};
  const interestedCareers = useMemo(
    () => results?.filter((item) => !!item?.interested) || [],
    [results],
  );
  const exploredCareerNamesList = useMemo(
    () => interestedCareers.flatMap(getCareerOptionsFromCareer).filter(Boolean),
    [interestedCareers],
  );
  const hasPersistedActionSteps =
    actionPlanState.getTerms.data?.has_action_plan === true ||
    (Array.isArray(actionPlanState.getTerms.data?.action_plans) &&
      actionPlanState.getTerms.data.action_plans.length > 0);
  const isPlanStarted =
    actionPlanState.getTerms.success &&
    (planUiStarted || hasPersistedActionSteps);
  const currentPlanGenerationStep = PLAN_GENERATION_STEPS[planGenerationStep];
  const isPlanGenerationComplete =
    planGenerationStep === PLAN_GENERATION_STEPS.length - 1;
  const isEmpty = useMemo(
    () =>
      Object.entries(safeStrengths).every(([key, value]) => {
        if (key === "first_name") return true;
        return (
          value === "" ||
          (Array.isArray(value) && value.length === 0) ||
          value == null
        );
      }),
    [safeStrengths],
  );
  const strengthCards = useMemo(
    () => ({
      Shine: [
        { title: "Where You Shine", list: safeStrengths?.top_traits || [] },
        { text: safeStrengths?.traits_insights || "" },
        {
          head: "Choose one and try leaning on it when you face a challenge. Notice what happens.",
          icon: LightBulbIco,
        },
      ],
      Activity: [
        {
          title: "Activities You Enjoy",
          list: safeStrengths?.top_activities || [],
        },
        { text: safeStrengths?.activities_insights || "" },
        {
          head: "Think about how you’ve already been using these. Where did it make a difference?",
          icon: LightBulbIco,
        },
      ],
      DoWell: [
        { title: "Things You Do Well", list: safeStrengths?.top_skills || [] },
        { text: safeStrengths?.skills_insights || "" },
        {
          head: "How could these help with something you’re working on? Challenge yourself to notice when you use it.",
          icon: LightBulbIco,
        },
      ],
    }),
    [safeStrengths],
  );

  const careerOptions = useMemo(() => {
    const names = exploredCareerNamesList;
    const taskCareers = actionPlanTerms
      .flatMap((term) => term.tasks.map((task) => task.career))
      .filter(Boolean);
    const ideaCareers = actionPlanIdeas
      .map((idea) => idea.career)
      .filter(Boolean);

    return Array.from(
      new Set(["Any Career", ...names, ...taskCareers, ...ideaCareers]),
    );
  }, [actionPlanIdeas, actionPlanTerms, exploredCareerNamesList]);
  const hasCompletedAssessment = !!results?.length;
  const hasSavedCareer = interestedCareers.length > 0;
  const completedTimePeriods = actionPlanTerms.filter(
    (term) => term.tasks.length > 0,
  ).length;
  const hasMatchingCareerStep = actionPlanTerms.some((term) =>
    term.tasks.some((task) =>
      exploredCareerNamesList.some(
        (career) =>
          String(career).trim().toLowerCase() ===
          String(task.career).trim().toLowerCase(),
      ),
    ),
  );
  const companionId = useSelector(
    (state) => state.aiCompanionsReducer.aiAgentId,
  );
  
  // const hasAccountabilityPartner = ACCOUNTABILITY_PARTNER_KEYS.some((key) =>
  //   hasFilledValue(user?.[key]),
  // );
  const progress = !isPlanStarted
    ? 0
    : !hasMatchingCareerStep || completedTimePeriods === 0
      ? 50
      : completedTimePeriods === 1
        ? 80
        : 100;
  const actionPlanIdeasStorageKey = user?.id
    ? `${ACTION_PLAN_IDEAS_STORAGE_KEY}_${user.id}`
    : ACTION_PLAN_IDEAS_STORAGE_KEY;

  useEffect(() => {
    dispatch(initCompanions({ name__iexact: "Career Buddy", page:1 }));
    dispatch(initStrengths());
    setHasRequestedTerms(true);
    dispatch(initGetTerms());

  }, [dispatch]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    const started = LocalStorage.getFromLocalStorage(
      `${ACTION_PLAN_UI_STARTED_KEY}_${user.id}`,
    );
    if (started) {
      setPlanUiStarted(true);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !hasPersistedActionSteps) {
      return;
    }
    setPlanUiStarted(true);
    LocalStorage.setToLocalStorage(
      `${ACTION_PLAN_UI_STARTED_KEY}_${user.id}`,
      true,
    );
  }, [user?.id, hasPersistedActionSteps]);

  useEffect(() => {
    setDirectionRating(user?.certainty ? Number(user.certainty) : null);
  }, [user?.certainty]);
  useEffect(() => {
    if (!isPlanGenerationOpen) {
      setPlanGenerationStep(0);
      return undefined;
    }

    const timers = PLAN_GENERATION_STEPS.slice(1).map((_, index) =>
      setTimeout(
        () => {
          setPlanGenerationStep(index + 1);
        },
        (index + 1) * 3000,
      ),
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isPlanGenerationOpen]);

  useEffect(() => {
    const { setTerms } = actionPlanState;

    if (setTerms.success) {
      const savedTaskId = getSavedTaskId(setTerms.data);
      const clientTaskId = setTerms.data?.clientId;

      if (clientTaskId && savedTaskId) {
        setActionPlanTerms((currentTerms) =>
          currentTerms.map((term) =>
            normalizeTerm({
              ...term,
              tasks: term.tasks.map((task) =>
                task.id === clientTaskId ? { ...task, id: savedTaskId } : task,
              ),
            }),
          ),
        );
      }

      setActionStepModalState({
        isLoading: false,
        error: null,
        isSuccess: true,
      });

      const timer = setTimeout(() => {
        handleCloseActionStepModal();
        setActionStepModalState({
          isLoading: false,
          error: null,
          isSuccess: false,
        });
      }, 500);

      return () => clearTimeout(timer);
    } else if (setTerms.error) {
      setActionStepModalState({
        isLoading: false,
        error: setTerms.error,
        isSuccess: false,
      });
    } else if (setTerms.loading) {
      setActionStepModalState({
        isLoading: true,
        error: null,
        isSuccess: false,
      });
    }
  }, [actionPlanState]);

  useEffect(() => {
    const storedIdeas = LocalStorage.getFromLocalStorage(
      actionPlanIdeasStorageKey,
    );

    if (!companionId) {
      setActionPlanIdeas(TERMS_DRAG_DROP);
      return;
    }

    if (Array.isArray(storedIdeas) && storedIdeas.length) {
      setActionPlanIdeas(storedIdeas);
      return;
    }

    setActionPlanIdeas([]);
  }, [companionId, actionPlanIdeasStorageKey]);

  useEffect(() => {
    const generated = mapGeneratedIdeaList(actionPlanState.generateIdeas?.data);

    if (actionPlanState.generateIdeas.loading && !generated.length) {
      setActionPlanIdeas([]);
      return;
    }
    if (actionPlanState.generateIdeas?.error) {
      setActionPlanIdeas(TERMS_DRAG_DROP);
      return;
    }
    if (generated.length) {
      setActionPlanIdeas(generated);
    }
  }, [
    actionPlanState.generateIdeas.data,
    actionPlanState.generateIdeas.loading,
  ]);

  useEffect(() => {
    if (companionId) {
      const data = actionPlanIdeas?.length
        ? actionPlanIdeas?.[0]?.isStatic
          ? []
          : actionPlanIdeas
        : [];
      LocalStorage.setToLocalStorage(actionPlanIdeasStorageKey, data);
    }
  }, [actionPlanIdeas, companionId, actionPlanIdeasStorageKey]);

  useEffect(() => () => cleanupTouchIdeaDrag(), []);

  useEffect(() => {
    if (!actionPlanState.getTerms.success) {
      return;
    }

    const terms = mapApiTerms(actionPlanState.getTerms.data);
    setActionPlanTerms(terms);
  }, [actionPlanState.getTerms.data, actionPlanState.getTerms.success]);

  useEffect(() => {
    actionPlanTermsRef.current = actionPlanTerms;
  }, [actionPlanTerms]);

  useEffect(() => () => cleanupTouchTaskDrag(), []);

  const normalizeTerm = (term) => ({
    ...term,
    tasks: normalizeTaskOrder(sortTasksByRanking(term.tasks || [])),
  });

  const updateTerm = (termId, updater) => {
    setActionPlanTerms((currentTerms) => {
      const nextTerms = currentTerms.map((term) =>
        term.id === termId ? normalizeTerm(updater(term)) : term,
      );

      actionPlanTermsRef.current = nextTerms;
      return nextTerms;
    });
  };

  const handleStartNew = (fromAssessment = false) => {
    if (!isErrorPricing.code_active) {
      openAffiliateCodeModal();
      return;
    }

    router(fromAssessment ? "/profile/recommendations" : "/scenario/0");
  };

  const handleGeneratePlan = () => {
    if (!hasPersistedActionSteps) {
      setPlanUiStarted(false);
      if (user?.id) {
        LocalStorage.setToLocalStorage(
          `${ACTION_PLAN_UI_STARTED_KEY}_${user.id}`,
          false,
        );
      }
    }
    setIsPlanGenerationOpen(true);
    if (companionId && interestedCareers.length) {
      dispatch(
        generateIdeas({
          prompt: buildGenerateIdeasPrompt(user, interestedCareers),
          companionId,
        }),
      );
    }
  };

  const beginActionPlanUi = () => {
    setIsPlanGenerationOpen(false);
    if (user?.id) {
      LocalStorage.setToLocalStorage(
        `${ACTION_PLAN_UI_STARTED_KEY}_${user.id}`,
        true,
      );
    }
    setPlanUiStarted(true);
    setActionPlanTerms(createEmptyActionPlanTerms());
    dispatch(initGetTerms());
  };

  const handleDirectionRatingChange = (rating) => {
    setDirectionRating(rating);

    const params = { ...user, certainty: rating };
    delete params.affiliate_code;
    delete params.profile_image;

    dispatch(initSetProfile(params));
  };

  const handleToggleAccordion = (termId) => {
    setOpenAccordionId((currentId) => (currentId === termId ? "" : termId));
  };

  const handleAddSearchCareer = () => {
    setIsOpenAddSearchCareer((isOpen) => !isOpen);
  };

  const handleUpdateTaskStatus = (termId, taskId, status) => {
    dispatch(setTerms({ status }, taskId));
    updateTerm(termId, (term) => ({
      ...term,
      tasks: term.tasks.map((task) =>
        task.id === taskId ? { ...task, status } : task,
      ),
    }));
  };

  const handleRemoveIdea = (ideaId) => {
    setActionPlanIdeas((currentIdeas) =>
      currentIdeas.filter((idea) => idea.id !== ideaId),
    );
  };

  const createTaskFromIdea = (termId, idea) => {
    const clientTaskId = `${termId}-${idea.id}-${Date.now()}`;

    updateTerm(termId, (term) => ({
      ...term,
      tasks: [
        ...term.tasks,
        createTask({
          id: clientTaskId,
          title: idea.title,
          career: idea.career,
        }),
      ],
    }));

    setActionPlanIdeas((currentIdeas) =>
      currentIdeas.filter((currentIdea) => currentIdea.id !== idea.id),
    );
    setOpenAccordionId(termId);

    dispatch(
      setTerms(
        {
          idea: idea.title,
          linked_career: idea.career,
          suggested_bucket: termId,
          status: DEFAULT_TASK_STATUS,
        },
        null,
        clientTaskId,
      ),
    );

    return clientTaskId;
  };

  const cleanupTouchIdeaDrag = () => {
    const cleanup = touchIdeaDragRef.current?.cleanup;

    if (typeof cleanup === "function") {
      cleanup();
    }

    touchIdeaDragRef.current = null;
    setTouchDraggedIdeaId(null);
  };

  const validateActionStepForm = (form) => {
    const errors = {};

    if (!form.idea.trim()) {
      errors.idea = "Action step is required.";
    }

    if (!form.linked_career) {
      errors.linked_career = "Please select a career.";
    }

    if (!form.suggested_bucket) {
      errors.suggested_bucket = "Please select when this should happen.";
    }

    if (!form.status) {
      errors.status = "Please select a status.";
    }

    return errors;
  };

  const handleOpenActionStepModal = (termId) => {
    setSelectedIdeaId(null);
    setActionStepModalTermId(termId);
    setActionStepForm({
      ...ACTION_STEP_INITIAL_FORM,
      linked_career: careerOptions[0] || "",
      suggested_bucket: termId,
    });
    setActionStepErrors({});
    setActionStepEditingTask(null);
    setActionStepModalState({
      isLoading: false,
      error: null,
      isSuccess: false,
    });
  };

  const handleOpenEditActionStepModal = (termId, task) => {
    setSelectedIdeaId(null);
    setActionStepModalTermId(termId);
    setActionStepForm({
      idea: task.title,
      linked_career: task.career,
      suggested_bucket: termId,
      status: task.status,
    });
    setActionStepErrors({});
    setActionStepEditingTask({ termId, taskId: task.id });
    setActionStepModalState({
      isLoading: false,
      error: null,
      isSuccess: false,
    });
  };

  const handleCloseActionStepModal = () => {
    setSelectedIdeaId(null);
    setActionStepModalTermId("");
    setActionStepForm(ACTION_STEP_INITIAL_FORM);
    setActionStepErrors({});
    setActionStepEditingTask(null);
    setActionStepModalState({
      isLoading: false,
      error: null,
      isSuccess: false,
    });
  };

  const handleChangeActionStepForm = (event) => {
    const { name, value } = event.target;

    setActionStepForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
    setActionStepErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const handleSaveActionStep = (event) => {
    event.preventDefault();

    const errors = validateActionStepForm(actionStepForm);
    const clientTaskId = actionStepEditingTask
      ? null
      : `${actionStepForm.suggested_bucket}-${Date.now()}`;

    if (Object.keys(errors).length) {
      setActionStepErrors(errors);
      return;
    }

    if (actionStepEditingTask) {
      setActionPlanTerms((currentTerms) =>
        currentTerms.map((term) => {
          const isOriginalTerm = term.id === actionStepEditingTask.termId;
          const isSelectedTerm = term.id === actionStepForm.suggested_bucket;

          if (isOriginalTerm && !isSelectedTerm) {
            return normalizeTerm({
              ...term,
              tasks: term.tasks.filter(
                (task) => task.id !== actionStepEditingTask.taskId,
              ),
            });
          }

          if (isSelectedTerm) {
            const editedTask = createTask({
              id: actionStepEditingTask.taskId,
              title: actionStepForm.idea.trim(),
              career: actionStepForm.linked_career,
              status: actionStepForm.status,
            });
            const hasTask = term.tasks.some(
              (task) => task.id === actionStepEditingTask.taskId,
            );

            return normalizeTerm({
              ...term,
              tasks: hasTask
                ? term.tasks.map((task) =>
                    task.id === actionStepEditingTask.taskId
                      ? editedTask
                      : task,
                  )
                : [...term.tasks, editedTask],
            });
          }

          return term;
        }),
      );
    } else {
      updateTerm(actionStepForm.suggested_bucket, (term) => ({
        ...term,
        tasks: [
          ...term.tasks,
          createTask({
            id: clientTaskId,
            title: actionStepForm.idea.trim(),
            career: actionStepForm.linked_career,
            status: actionStepForm.status,
          }),
        ],
      }));

      if (selectedIdeaId) {
        setActionPlanIdeas((currentIdeas) =>
          currentIdeas.filter((idea) => idea.id !== selectedIdeaId),
        );
        setSelectedIdeaId(null);
      }
    }
    setOpenAccordionId(actionStepForm.suggested_bucket);
    dispatch(
      setTerms(
        { ...actionStepForm },
        actionStepEditingTask?.taskId,
        clientTaskId,
      ),
    );
  };

  const handleOpenActionStepModalFromIdea = (idea) => {
    const defaultBucket =
      actionPlanTerms.find((term) => term.id === openAccordionId)?.id ||
      actionPlanTerms[0]?.id ||
      "short_term";

    setSelectedIdeaId(idea.id);
    setOpenAccordionId(defaultBucket);
    setActionStepModalTermId(defaultBucket);
    setActionStepForm({
      ...ACTION_STEP_INITIAL_FORM,
      idea: idea.title || "",
      linked_career: idea.career || careerOptions[0] || "",
      suggested_bucket: defaultBucket,
      status: DEFAULT_TASK_STATUS,
    });
    setActionStepErrors({});
    setActionStepEditingTask(null);
    setActionStepModalState({
      isLoading: false,
      error: null,
      isSuccess: false,
    });
  };

  const handleOpenDeleteModal = (termId, task) => {
    setTaskToDelete({ termId, task });
  };

  const handleCloseDeleteModal = () => {
    setTaskToDelete(null);
  };

  const handleConfirmDeleteTask = () => {
    if (!taskToDelete) {
      return;
    }
    updateTerm(taskToDelete.termId, (term) => ({
      ...term,
      tasks: term.tasks.filter((task) => task.id !== taskToDelete.task.id),
    }));
    dispatch(deleteTerms(taskToDelete.task.id));
    handleCloseDeleteModal();
  };

  const handleDownloadPlan = () => {
    dispatch(downloadAction());
  };

  const handleSeeMoreStrength = (index) => {
    strengthSlidesRef.current[index]?.slickNext?.();
  };

  const handleIdeaDragStart = (event, ideaId) => {
    setDraggedIdeaId(ideaId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", ideaId);
  };

  const handleTouchIdeaStart = (event, idea) => {
    const touch = event.touches?.[0];

    if (!touch) {
      return;
    }

    event.preventDefault();
    cleanupTouchIdeaDrag();
    setTouchDraggedIdeaId(idea.id);
    setDragOverTermId("");

    const dragState = {
      idea,
      targetTermId: "",
      cleanup: null,
    };

    const handleMove = (moveEvent) => {
      const activeTouch = moveEvent.touches?.[0];

      if (!activeTouch) {
        return;
      }

      moveEvent.preventDefault();

      const elementUnderTouch = document.elementFromPoint(
        activeTouch.clientX,
        activeTouch.clientY,
      );
      const targetRow = elementUnderTouch?.closest?.(
        "[data-action-plan-task-row='true']",
      );
      const targetTermId =
        targetRow?.dataset?.actionPlanTermId ||
        elementUnderTouch?.closest?.("[data-action-plan-term-id]")?.dataset
          ?.actionPlanTermId;

      if (!targetTermId || targetTermId === dragState.targetTermId) {
        return;
      }

      dragState.targetTermId = targetTermId;
      setDragOverTermId(targetTermId);
    };

    const finish = (finishEvent) => {
      const finalTouch = finishEvent?.changedTouches?.[0];

      if (finalTouch?.clientX != null && finalTouch?.clientY != null) {
        const elementUnderTouch = document.elementFromPoint(
          finalTouch.clientX,
          finalTouch.clientY,
        );
        const targetRow = elementUnderTouch?.closest?.(
          "[data-action-plan-task-row='true']",
        );
        const targetTermId =
          targetRow?.dataset?.actionPlanTermId ||
          elementUnderTouch?.closest?.("[data-action-plan-term-id]")?.dataset
            ?.actionPlanTermId;

        if (targetTermId) {
          createTaskFromIdea(targetTermId, dragState.idea);
        }
      }

      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", finish);
      document.removeEventListener("touchcancel", finish);
      setTouchDraggedIdeaId(null);
      setDragOverTermId("");

      if (touchIdeaDragRef.current?.cleanup === finish) {
        touchIdeaDragRef.current = null;
      }
    };

    dragState.cleanup = finish;
    touchIdeaDragRef.current = dragState;

    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", finish, { once: true });
    document.addEventListener("touchcancel", finish, { once: true });
  };

  const handleIdeaDragEnd = () => {
    setDraggedIdeaId(null);
    setDragOverTermId("");
  };

  const handleDesktopTaskDragStart = (event, termId, taskId) => {
    setDraggedTaskId(taskId);
    setDraggedTaskTermId(termId);
    setDragOverTaskId(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ termId, taskId }),
    );
  };

  const handleDesktopTaskDragOver = (event, termId, taskId) => {
    event.preventDefault();

    if (draggedTaskId && draggedTaskId !== taskId) {
      setDragOverTaskId(taskId);
    } else if (draggedTaskTermId === termId) {
      setDragOverTaskId(taskId);
    }
  };

  const handleDesktopTaskDrop = (event, termId, targetTaskId) => {
  event.preventDefault();

  const rawTaskData = event.dataTransfer.getData("text/plain");
  let parsedTaskData = null;

  if (rawTaskData) {
    try {
      parsedTaskData = JSON.parse(rawTaskData);
    } catch {
      parsedTaskData = null;
    }
  }

  const sourceTermId = parsedTaskData?.termId || draggedTaskTermId;
  const sourceTaskId = parsedTaskData?.taskId || draggedTaskId;

  if (!sourceTermId || !sourceTaskId || sourceTermId !== termId || sourceTaskId === targetTaskId) {
    setDraggedTaskId(null);
    setDraggedTaskTermId(null);
    setDragOverTaskId(null);
    return;
  }

  event.stopPropagation();

  // ✅ Last position fix
  const currentTerm = actionPlanTermsRef.current.find(t => t.id === termId);
  const tasks = currentTerm?.tasks || [];
  const lastTaskId = tasks[tasks.length - 1]?.id;
  const draggedIndex = tasks.findIndex(t => t.id === sourceTaskId);
  const targetIndex = tasks.findIndex(t => t.id === targetTaskId);
  const resolvedTargetId = (targetTaskId === lastTaskId && draggedIndex < targetIndex) ? null : targetTaskId;

  handleMoveTask(termId, sourceTaskId, resolvedTargetId);
  setDraggedTaskId(null);
  setDraggedTaskTermId(null);
  setDragOverTaskId(null);
  handlePersistTaskOrder(termId);
};

  const handleDesktopTaskDragEnd = () => {
    setDraggedTaskId(null);
    setDraggedTaskTermId(null);
    setDragOverTermId(null)
    setDragOverTaskId(null);
  };

  const cleanupTouchTaskDrag = () => {
    const cleanup = touchTaskDragRef.current?.cleanup;

    if (typeof cleanup === "function") {
      cleanup();
    }

    touchTaskDragRef.current = null;
    setTouchDraggedTaskId(null);
    setTouchDropTargetTaskId(null);
  };

  const handleMoveTask = (termId, draggedTaskId, targetTaskId) => {
    updateTerm(termId, (term) => ({
      ...term,
      tasks: moveTaskInList(term.tasks, draggedTaskId, targetTaskId),
    }));
  };

  const handlePersistTaskOrder = (termId) => {
    const currentTerm = actionPlanTermsRef.current.find((term) => term.id === termId);
    const orderedIds = (currentTerm?.tasks || [])
      .map((task) => Number(task.id))
      .filter(Number.isFinite);

    if (orderedIds.length) {
      dispatch(
        reorderActionTerm({
          bucket: termId,
          ordered_ids: orderedIds,
        }),
      );
    }
  };

  const handleTouchTaskStart = (event, termId, taskId) => {
  const touch = event.touches?.[0];
  if (!touch) return;

  event.preventDefault();
  cleanupTouchTaskDrag();
  setTouchDraggedTaskId(taskId);

  const dragState = {
    termId,
    taskId,
    lastTargetTaskId: null,
    pendingTargetTaskId: null,
    cleanup: null,
  };

  const handleMove = (moveEvent) => {
    const activeTouch = moveEvent.touches?.[0];
    if (!activeTouch) return;
    moveEvent.preventDefault();

    const elementUnderTouch = document.elementFromPoint(activeTouch.clientX, activeTouch.clientY);
    const targetRow = elementUnderTouch?.closest?.("[data-action-plan-task-row='true']");
    const targetTermId = targetRow?.dataset?.actionPlanTermId;
    const targetTaskId = targetRow?.dataset?.actionPlanTaskId;

    if (!targetRow || targetTermId !== dragState.termId || !targetTaskId ||
      targetTaskId === dragState.taskId || targetTaskId === dragState.lastTargetTaskId) {
      return;
    }

    // ✅ Last position fix
    const currentTasks = actionPlanTermsRef.current.find(t => t.id === dragState.termId)?.tasks || [];
    const lastTaskId = currentTasks[currentTasks.length - 1]?.id;
    const draggedIdx = currentTasks.findIndex(t => t.id === dragState.taskId);
    const targetIdx = currentTasks.findIndex(t => t.id === targetTaskId);
    const resolvedTargetId = (targetTaskId === lastTaskId && draggedIdx < targetIdx) ? null : targetTaskId;

    dragState.lastTargetTaskId = targetTaskId;
    setTouchDropTargetTaskId(targetTaskId);
    dragState.pendingTargetTaskId = targetTaskId;
    handleMoveTask(dragState.termId, dragState.taskId, resolvedTargetId);
  };

  const finish = (finishEvent) => {
    const finalTouch = finishEvent?.changedTouches?.[0];

    if (finalTouch) {
      const elementUnderTouch = document.elementFromPoint(finalTouch.clientX, finalTouch.clientY);
      const targetRow = elementUnderTouch?.closest?.("[data-action-plan-task-row='true']");
      const targetTermId = targetRow?.dataset?.actionPlanTermId;
      const targetTaskId = targetRow?.dataset?.actionPlanTaskId;

      if (targetRow && targetTermId === dragState.termId && targetTaskId &&
        targetTaskId !== dragState.taskId && targetTaskId !== dragState.pendingTargetTaskId) {

        // ✅ Last position fix
        const currentTasks = actionPlanTermsRef.current.find(t => t.id === dragState.termId)?.tasks || [];
        const lastTaskId = currentTasks[currentTasks.length - 1]?.id;
        const draggedIdx = currentTasks.findIndex(t => t.id === dragState.taskId);
        const targetIdx = currentTasks.findIndex(t => t.id === targetTaskId);
        const resolvedTargetId = (targetTaskId === lastTaskId && draggedIdx < targetIdx) ? null : targetTaskId;

        handleMoveTask(dragState.termId, dragState.taskId, resolvedTargetId);
        dragState.pendingTargetTaskId = targetTaskId;
        setTouchDropTargetTaskId(targetTaskId);
      }
    }

    document.removeEventListener("touchmove", handleMove);
    document.removeEventListener("touchend", finish);
    document.removeEventListener("touchcancel", finish);
    setTouchDropTargetTaskId(null);
    setTouchDraggedTaskId(null);
    handlePersistTaskOrder(termId);

    if (touchTaskDragRef.current?.cleanup === finish) {
      touchTaskDragRef.current = null;
    }
  };

  dragState.cleanup = finish;
  touchTaskDragRef.current = dragState;

  document.addEventListener("touchmove", handleMove, { passive: false });
  document.addEventListener("touchend", finish, { once: true });
  document.addEventListener("touchcancel", finish, { once: true });
};

  const handleTermDragOver = (event, termId) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverTermId(termId);
  };

  const handleTermDragLeave = (event, termId) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragOverTermId((currentId) => (currentId === termId ? "" : currentId));
    }
  };

  const handleDropIdea = (event, termId) => {
    event.preventDefault();
    const droppedIdeaId =
      event.dataTransfer.getData("text/plain") || draggedIdeaId;
      
    const droppedIdea = actionPlanIdeas.find(
      (idea) => String(idea.id) === String(droppedIdeaId),
    );
    
    if (!droppedIdea) {
      handleIdeaDragEnd();
      return;
    }

    createTaskFromIdea(termId, droppedIdea);
    handleIdeaDragEnd();
  };
  
  return (
    <>
      <div className="aiCompanions__container actionPlan">
          <ActionPlanHeader
            progress={progress}
            showProgressInfo={showProgressInfo}
            isPlanStarted={isPlanStarted}
            isLoading={!hasRequestedTerms || actionPlanState.getTerms.loading}
            handleDownloadPlan={handleDownloadPlan}
            onBack={() => router(-1)}
            onToggleProgressInfo={() => setShowProgressInfo((prev) => !prev)}
          />

          {!hasRequestedTerms || actionPlanState.getTerms.loading ? (
            <Loader />
          ) : isPlanStarted ? (
            <>
              <CareersSection
                careers={interestedCareers}
                onAddCareer={handleAddSearchCareer}
              />
              <NextStepsSection
                isRegenerating={actionPlanIdeas?.length}
                isLoading={actionPlanState.generateIdeas.loading}
                interestedCareers={interestedCareers}
                companionId={companionId}
              />
              <div className="actionPlan__section">
                <ActionIdeas
                  isTopSpace={!interestedCareers?.length}
                  ideas={actionPlanIdeas}
                  draggedIdeaId={draggedIdeaId}
                  touchDraggedIdeaId={touchDraggedIdeaId}
                  onDragStart={handleIdeaDragStart}
                  onDragEnd={handleIdeaDragEnd}
                  onTouchStart={handleTouchIdeaStart}
                  onClickIdea={handleOpenActionStepModalFromIdea}
                  onRemoveIdea={handleRemoveIdea}
                />
                <div className="actionPlan__accordians">
                  {actionPlanTerms.map((term) => (
                    <ActionPlanTerm
                    key={term.id}
                    term={term}
                    isOpen={openAccordionId === term.id}
                    isDragOver={dragOverTermId === term.id}
                    onAddTask={handleOpenActionStepModal}
                      onDropIdea={handleDropIdea}
                      onDragOver={handleTermDragOver}
                      onDragLeave={handleTermDragLeave}
                    onToggle={handleToggleAccordion}
                    onEditTask={handleOpenEditActionStepModal}
                    onOpenDeleteModal={handleOpenDeleteModal}
                    onUpdateTaskStatus={handleUpdateTaskStatus}
                    draggedTaskId={draggedTaskId}
                    dragOverTaskId={dragOverTaskId}
                    onDesktopDragStart={handleDesktopTaskDragStart}
                    onDesktopDragOver={handleDesktopTaskDragOver}
                    onDesktopDrop={handleDesktopTaskDrop}
                    onDesktopDragEnd={handleDesktopTaskDragEnd}
                    onTouchStart={handleTouchTaskStart}
                    touchDraggedTaskId={touchDraggedTaskId}
                    exploredCareerNames={exploredCareerNamesList}
                    ArrowPlayIco={ArrowPlayIco}
                  />
                  ))}
                </div>
              </div>
              {!isEmpty && <StrengthsSection
                openStrengthCard={openStrengthCard}
                onOpenCard={setOpenStrengthCard}
                onSeeMore={handleSeeMoreStrength}
                slidesRef={strengthSlidesRef}
                strengthCards={strengthCards}
              />}
              <DirectionRating
                value={directionRating}
                onChange={handleDirectionRatingChange}
              />
            </>
          ) : (
            <PlanEmptyState
              hasCompletedAssessment={hasCompletedAssessment}
              hasSavedCareer={hasSavedCareer}
              onGeneratePlan={handleGeneratePlan}
              onStartNew={handleStartNew}
            />
          )}
        </div>

      <PlanGenerationModal
        currentStep={currentPlanGenerationStep}
        isComplete={isPlanGenerationComplete}
        isOpen={isPlanGenerationOpen}
        canStartPlan={
          !companionId ||
          (!actionPlanState.generateIdeas.loading &&
            mapGeneratedIdeaList(actionPlanState.generateIdeas?.data).some(
              (idea) => String(idea.title || "").trim(),
            ))
        }
        onBack={() => setIsPlanGenerationOpen(false)}
        onClose={() => setIsPlanGenerationOpen(false)}
        onStartPlan={beginActionPlanUi}
      />
      <BaseModal
        className="Find__career__container"
        isOpen={isOpenAddSearchCareer}
        onClose={handleAddSearchCareer}
      >
        <FindCareer companion_id={companionId} />
      </BaseModal>
      <DeleteTaskModal
        taskToDelete={taskToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDeleteTask}
      />

      <ActionStepModal
        careerOptions={careerOptions}
        errors={actionStepErrors}
        form={actionStepForm}
        isOpen={!!actionStepModalTermId}
        isUpdate={!!actionStepEditingTask?.taskId}
        terms={actionPlanTerms}
        onChange={handleChangeActionStepForm}
        onClose={handleCloseActionStepModal}
        onSubmit={handleSaveActionStep}
        isLoading={actionStepModalState.isLoading}
        apiError={actionStepModalState.error}
        isSuccess={actionStepModalState.isSuccess}
      />
      <ChoosePlanFirstModal
        isOpened={choosePlanFirstOpen}
        onClose={closeChoosePlanFirstModal}
        onContinue={confirmChoosePlanFirstModal}
      />
      <AffiliateCodeModal
        {...affiliateCodeModal}
        onBack={closeAffiliateCodeModal}
        onChange={setAffiliateCode}
        onSuccessClose={() =>
          results?.length
            ? closeAffiliateCodeModal()
            : router(
                isErrorPricing.code_active ? "/scenario/0" : "/pricingplan",
              )
        }
        onPricingPlanClick={() => {
          closeAffiliateCodeModal();
          router("/pricingplan");
        }}
        onSubmit={submitAffiliateCode}
      />
    </>
  );
}

function ActionPlanHeader({
  progress,
  showProgressInfo,
  isPlanStarted,
  onBack,
  onToggleProgressInfo,
  handleDownloadPlan,
  isLoading,
}) {
  return (
    <>
      <div className="aiCompanions__backScoop">
        <button type="button" onClick={onBack}>
          <img src={ArrowIco} alt="back" /> Back
        </button>
      </div>
      <div className="actionPlan__container">
        <div className="aiCompanions__title">
          <h3>My Action Plan</h3>
          <p>Turn what you’ve discovered into your next steps</p>
        </div>
        {
          isLoading ? null:
          <>
        <div className="actionPlan__status">
          <div className="actionPlan__progress-wrap">
            <ActionPlanProgress
              value={progress}
              showPercentage={isPlanStarted}
            />
            <button
              type="button"
              className="actionPlan__progress-info"
              aria-label="Show plan progress info"
              aria-expanded={showProgressInfo}
              onClick={onToggleProgressInfo}
            >
              <Info size={20} strokeWidth={2.4} />
            </button>
            {showProgressInfo && (
              <div className="actionPlan__progress-message" role="status">
                {progress === 0
                  ? "Plan not started! Generate plan to move forward."
                  : progress === 50
                    ? "Plan started! Add a step to move forward."
                    : progress === 80
                      ? "You’ve started! Add one more to complete your plan."
                      : "Plan ready! You’re set — now take action."}
              </div>
            )}
          </div>
          <p className="actionPlan__status-text">
            {progress === 100
              ? "Plan ready"
              : progress >= 50
                ? `Plan Started`
                : "Not Started"}
          </p>
        </div>
        {isPlanStarted && (
          <button type="button" className="button" onClick={handleDownloadPlan}>
            <p className="button__text">Download</p>
          </button>
        )}
        </>
        }

      </div>
    </>
  );
}

function CareersSection({ careers, onAddCareer }) {
  return (
    <div className="actionPlan__section">
      <h3>Careers I’m Exploring</h3>
      <div className="Recommendations-cont">
        {careers.map((career) => (
          <Card key={career.id} el={career} isCareer={true} />
        ))}
      </div>
      <button
        type="button"
        className="actionPlan__btn--addCareer"
        onClick={onAddCareer}
      >
        <span>
          <Plus />
        </span>
        Add a Career
      </button>
    </div>
  );
}
function StrengthsSection({
  openStrengthCard,
  onOpenCard,
  onSeeMore,
  slidesRef,
  strengthCards,
}) {
  return (
    <div className="actionPlan__section">
      <h3>Strengths I’m Leaning On</h3>
      <div className="actionPlan__strengths-row">
        {STRENGTH_CARDS.map((strength, index) => (
          <ActionPlanStrengthFlipCard
            key={strength.alt}
            index={index}
            icon={strength.icon}
            alt={strength.alt}
            cardData={
              strengthCards[
                strength.alt === "Do well" ? "DoWell" : strength.alt
              ] || []
            }
            isOpen={openStrengthCard === strength.alt}
            onOpen={() => onOpenCard(strength.alt)}
            onClose={() => onOpenCard(null)}
            onSeeMore={() => onSeeMore(index)}
            slidesRef={slidesRef}
          />
        ))}
      </div>
    </div>
  );
}

function ActionPlanStrengthFlipCard({
  index,
  icon,
  alt,
  cardData,
  isOpen,
  onOpen,
  onClose,
  onSeeMore,
  slidesRef,
}) {
  const data = Array.isArray(cardData) ? cardData : [];

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      onClose();
    }
  };

  return (
    <div
      className={classNames("actionPlan__strength-card", {
        "is-open": isOpen,
      })}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onFocus={onOpen}
      onBlur={handleBlur}
      tabIndex={0}
      role="button"
      aria-label={`${alt} strength card`}
    >
      <div className="actionPlan__strength-card-inner">
        <div className="actionPlan__strength-card-face actionPlan__strength-card-face--front">
          <img src={icon} alt={alt} height={103} />
        </div>
        <div className="actionPlan__strength-card-face actionPlan__strength-card-face--back">
          <div className="actionPlan__strength-card-slider">
            <Slider
              className="actionPlan__strength-slider"
              ref={(slider) => {
                if (slidesRef?.current) {
                  slidesRef.current[index] = slider;
                }
              }}
              dots
              arrows={false}
              infinite
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={false}
            >
              {data.map((item, itemIndex) => (
                <ActionPlanStrengthSlide
                  key={`${alt}-${itemIndex}`}
                  item={item}
                />
              ))}
            </Slider>
          </div>
          <button
            type="button"
            className="actionPlan__strength-card-btn"
            onClick={onSeeMore}
          >
            See More
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionPlanStrengthSlide({ item }) {
  const [expandedLi, setExpandedLi] = useState({});
  const toggleLi = (key) => {
    setExpandedLi((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  return (
    <div className="actionPlan__strength-slide">
      {item.title && <h3>{item.title}</h3>}
      {item.list && (
        <ul className="strengths__list__ul">
          {(Array.isArray(item.list) ? item.list : []).map((li, i) => {
            const key = `${item.title}-${i}`;
            const isExpanded = expandedLi[key];
            const shouldTruncate = li.length > 25;
            return (
              <li
                style={{
                  whiteSpace:
                    isExpanded || !shouldTruncate ? "normal" : "nowrap",
                  overflow:
                    isExpanded || !shouldTruncate ? "visible" : "hidden",
                  textOverflow:
                    isExpanded || !shouldTruncate ? "clip" : "ellipsis",
                  cursor: shouldTruncate && !isExpanded ? "pointer" : "default",
                }}
                onClick={() => shouldTruncate && !isExpanded && toggleLi(key)}
                title={!isExpanded && shouldTruncate ? "Click to expand" : ""}
                key={`${li}-${i}`}
              >
                {li}
                {shouldTruncate && !isExpanded && <span>...</span>}
              </li>
            );
          })}
        </ul>
      )}
      {item.text && (
        <div className="strengths__list__des">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.text}</ReactMarkdown>
        </div>
      )}
      {item.head && <h4>{item.head}</h4>}
      {item.icon && (
        <img
          src={item.icon}
          className="strengths__list__ico"
          alt=""
          height={74}
        />
      )}
    </div>
  );
}

function DirectionRating({ value, onChange }) {
  return (
    <div className="actionPlan__section">
      <h3 className="actionPlan__section-title">
        How certain do you feel about your direction right now?
      </h3>
      <div
        className="actionPlan__rating"
        role="radiogroup"
        aria-label="Direction certainty rating"
      >
        <div className="actionPlan__rating-scale">
          {DIRECTION_RATING_OPTIONS.map((rating) => (
            <button
              type="button"
              className={classNames("actionPlan__rating-option", {
                "is-active": value === rating,
              })}
              key={rating}
              role="radio"
              aria-checked={value === rating}
              aria-label={`${rating} out of 10`}
              onClick={() => onChange(rating)}
            >
              <span className="actionPlan__rating-dot" />
              <span className="actionPlan__rating-number">{rating}</span>
            </button>
          ))}
        </div>
        <div className="actionPlan__rating-labels" aria-hidden="true">
          <span>low</span>
          <span>high</span>
        </div>
      </div>
    </div>
  );
}

// PlanEmptyState, PlanGenerationModal and related small components
// were moved to separate files for clarity and performance.

function DeleteTaskModal({ taskToDelete, onClose, onConfirm }) {
  return (
    <BaseModal
      className="action-plan-delete-modal"
      isOpen={!!taskToDelete}
      onClose={onClose}
      showClose={false}
    >
      <div className="action-plan-delete">
        <h4>Delete Action Item?</h4>
        <p>
          Are you sure you want to delete “{taskToDelete?.task.title}” from your
          action plan?
        </p>
        <div className="action-plan-delete__actions">
          <button
            type="button"
            className="action-plan-delete__btn action-plan-delete__btn--outline"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="action-plan-delete__btn action-plan-delete__btn--danger"
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </BaseModal>
  );
}

function ActionStepModal({
  careerOptions,
  errors,
  form,
  isOpen,
  isUpdate,
  terms,
  onChange,
  onClose,
  onSubmit,
  isLoading = false,
  apiError = null,
  isSuccess = false,
}) {
  return (
    <BaseModal
      className="action-step-modal"
      isOpen={isOpen}
      onClose={onClose}
      showClose={!isLoading}
    >
      <form className="action-step-form" onSubmit={onSubmit} noValidate>
        <img
          src={ActionStepIco}
          alt=""
          className="action-step-form__icon"
          aria-hidden="true"
        />
        <h4>Action Step</h4>

        <div className="action-step-form__field">
          <textarea
            name="idea"
            value={form.idea}
            aria-label="Action step"
            aria-invalid={!!errors.idea}
            onChange={onChange}
            disabled={isLoading}
          />
          {errors.idea && (
            <span className="action-step-form__error">{errors.idea}</span>
          )}
        </div>

        <div className="action-step-form__row">
          <div className="action-step-form__field">
            <div className="action-step-form__select action-step-form__select--career">
              <select
                name="linked_career"
                value={form.linked_career}
                aria-label="Career"
                aria-invalid={!!errors.linked_career}
                onChange={onChange}
                disabled={isLoading}
              >
                <option value="" disabled>
                  Career
                </option>
                {careerOptions.map((career) => (
                  <option value={career} key={career}>
                    {career}
                  </option>
                ))}
              </select>
              <ChevronDown size={22} strokeWidth={2.2} />
            </div>
            {errors.linked_career && (
              <span className="action-step-form__error">
                {errors.linked_career}
              </span>
            )}
          </div>

          <div className="action-step-form__field">
            <div className="action-step-form__select">
              <select
                name="suggested_bucket"
                value={form.suggested_bucket}
                aria-label="When"
                aria-invalid={!!errors.suggested_bucket}
                onChange={onChange}
                disabled={isLoading}
              >
                <option value="" disabled>
                  When?
                </option>
                {terms.map((term) => (
                  <option value={term.id} key={term.id}>
                    {term.title}
                  </option>
                ))}
              </select>
              <ChevronDown size={22} strokeWidth={2.2} />
            </div>
            {errors.suggested_bucket && (
              <span className="action-step-form__error">
                {errors.suggested_bucket}
              </span>
            )}
          </div>
        </div>

        <label className="action-step-form__label" htmlFor="action-step-status">
          Status
        </label>
        <div className="action-step-form__field">
          <div
            className={`action-step-form__select action-step-form__select--full action-step-form__select--${getStatusClassName(
              form.status,
            )}`}
          >
            <select
              id="action-step-status"
              name="status"
              value={form.status}
              aria-invalid={!!errors.status}
              onChange={onChange}
              disabled={isLoading}
            >
              {TASK_STATUS_OPTIONS.map((status) => (
                <option value={status.value} key={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <ChevronDown size={22} strokeWidth={2.2} />
          </div>
          {errors.status && (
            <span className="action-step-form__error">{errors.status}</span>
          )}
        </div>
        {isSuccess && (
          <div className="confirmationSuccess" role="alert">
            <span className="confirmationSuccess__Text">
              Terms {isUpdate ? "Updated" : "Added"} Successfully!
            </span>
          </div>
        )}
        {apiError && (
          <div className="action-step-form__api-error" role="alert">
            <span className="action-step-form__error-text">{apiError}</span>
          </div>
        )}
        <div className="action-step-form__actions">
          <button
            type="button"
            className="action-step-form__btn action-step-form__btn--outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="action-step-form__btn action-step-form__btn--primary"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : isUpdate ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </BaseModal>
  );
}
